import moment from 'moment'
import attendanceService from '@/services/attendance-service'
import { isWFHLeaveType, resolveMemberEmpId } from '@/utils/attendance-leave-types'
import userService from '@/services/user-service'
import userProbationService from '@/services/user-probation-service'

export default {
  methods: {
    // ============================================
    // TEAM DATA LOADING
    // ============================================
    /**
     * Load today's punch-attendance for the scoped team (Present/Absent KPIs).
     * Prefers one date-range call; falls back to batched per-member fetches.
     */
    async loadTeamTodayAttendance() {
      const today = moment().format('YYYY-MM-DD')
      let memberIds = []
      if (this.accountManagerTeamMemberIds && this.accountManagerTeamMemberIds.length > 0) {
        memberIds = this.accountManagerTeamMemberIds.map(id => String(id))
      } else {
        memberIds = (this.teamMembers || [])
          .map(m => resolveMemberEmpId(m))
          .filter(Boolean)
          .map(id => String(id))
      }
      if (memberIds.length === 0) {
        this.teamTodayPunchRecords = []
        return
      }

      const idSet = new Set(memberIds)
      try {
        const res = await attendanceService.getPunchAttendanceByDateRange(today, today, null)
        if (res && res.status === 'OK' && Array.isArray(res.data)) {
          // Unscoped call succeeded — use team subset (may be empty = nobody punched).
          this.teamTodayPunchRecords = res.data.filter(r => r && r.emp_id && idSet.has(String(r.emp_id)))
          return
        }
      } catch (err) {
        console.warn('Team today punch (unscoped) failed, trying per-member:', err)
      }

      // Fallback: fetch today per team member (batched)
      const chunk = (arr, size) =>
        Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, i * size + size))
      const records = []
      for (const batchIds of chunk(memberIds, 5)) {
        const batchResults = await Promise.allSettled(batchIds.map(async empId => {
          try {
            const res = await attendanceService.getPunchAttendanceByDateRange(today, today, empId)
            if (res && res.status === 'OK' && Array.isArray(res.data)) {
              return res.data.map(r => ({ ...r, emp_id: r.emp_id || empId }))
            }
            return []
          } catch (e) {
            return []
          }
        }))
        batchResults.forEach(r => {
          if (r.status === 'fulfilled' && Array.isArray(r.value)) {
            records.push(...r.value)
          }
        })
      }
      this.teamTodayPunchRecords = records
    },

    async loadAllTeamLeaves() {
      this.loadingLeaves = true
      try {
        const teamEmpIds = (this.teamMembers || [])
          .map(m => resolveMemberEmpId(m))
          .filter(Boolean)

        if (!teamEmpIds.includes(this.emp_id)) {
          teamEmpIds.push(this.emp_id)
        }

        const filtersForLeaves = this.activeFilters ? { ...this.activeFilters } : {}
        delete filtersForLeaves.status

        const chunk = (arr, size) =>
          Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, i * size + size))

        const leavesArrays = []
        for (const batchIds of chunk(teamEmpIds, 5)) {
          const batchResults = await Promise.allSettled(batchIds.map(async empId => {
            try {
              const res = await attendanceService.getMyLeaves(empId, filtersForLeaves)
              if (res && res.status === 'OK' && res.data) {
                return res.data.map(leave => ({ ...leave, emp_id: empId }))
              }
              return []
            } catch (err) {
              console.error(`Error loading leaves for ${empId}:`, err)
              return []
            }
          }))
          leavesArrays.push(...batchResults.map(r => (r.status === 'fulfilled' ? r.value : [])))
        }

        const statsArrays = []
        for (const batchIds of chunk(teamEmpIds, 5)) {
          const batchResults = await Promise.allSettled(batchIds.map(async empId => {
            try {
              const res = await attendanceService.getLeaveStats(empId)
              if (res && res.status === 'OK' && res.data) {
                return { emp_id: empId, ...res.data }
              }
              return { emp_id: empId, leaveBalance: 0, leavesTaken: 0, WFHCount: 0 }
            } catch (err) {
              console.error(`Error loading stats for ${empId}:`, err)
              return { emp_id: empId, leaveBalance: 0, leavesTaken: 0, WFHCount: 0 }
            }
          }))
          statsArrays.push(...batchResults.map(r => (r.status === 'fulfilled' ? r.value : { emp_id: null, leaveBalance: 0, leavesTaken: 0, WFHCount: 0 })))
        }

        const configArrays = []
        for (const batchIds of chunk(teamEmpIds, 5)) {
          const batchResults = await Promise.allSettled(batchIds.map(async empId => {
            try {
              const res = await attendanceService.getEmployeeConfig(empId)
              if (res && res.status === 'OK' && res.data) {
                return { emp_id: empId, config: res.data }
              }
              return { emp_id: empId, config: null }
            } catch (err) {
              console.error(`Error loading config for ${empId}:`, err)
              return { emp_id: empId, config: null }
            }
          }))
          configArrays.push(...batchResults.map(r => (r.status === 'fulfilled' ? r.value : { emp_id: null, config: null })))
        }

        this.teamMemberLeaveBalances = statsArrays || []
        this.teamMemberConfigs = configArrays || []

        this.allTeamLeaves = leavesArrays
          .flat()
          .sort((a, b) => {
            const dateA = moment(a.from_date || a.applied_date || a.created_at)
            const dateB = moment(b.from_date || b.applied_date || b.created_at)
            return dateB.diff(dateA)
          })

        await this.loadRegularizationCases(this.emp_id)

        const todayMoment = moment()
        let totalLeavesAvailed = 0
        let totalWFHCount = 0
        let totalRegularization = 0
        let membersOnLeave = 0

        // Derive leaves availed from leave records (exclude WFH; API stats may include WFH)
        this.allTeamLeaves.forEach(leave => {
          if ((leave.status || '').toLowerCase() !== 'approved') return
          const leaveType = (leave.leave_type || leave.type || '').toLowerCase()
          const isWFH = isWFHLeaveType(leave.leave_type || leave.type)
          if (isWFH) {
            totalWFHCount += leave.total_days || 1
          } else {
            totalLeavesAvailed += leave.total_days || 1
          }
        })

        // Exclude today's local calendar day from team regularization count
        const todayLocal = moment().format('YYYY-MM-DD')
        totalRegularization = this.regularizationCases
          ? this.regularizationCases.filter(reg => {
            if (!reg) return false
            const caseDate = reg.datestring || reg.date
            if (!caseDate) return true
            return moment(caseDate).format('YYYY-MM-DD') !== todayLocal
          }).length
          : 0

        const activeLeaves = this.allTeamLeaves.filter(leave => {
          if (leave.status !== 'Approved') return false
          const fromDate = moment(leave.from_date)
          const toDate = moment(leave.to_date)
          return todayMoment.isSameOrAfter(fromDate, 'day') && todayMoment.isSameOrBefore(toDate, 'day')
        })

        const uniqueEmployeesOnLeave = new Set(activeLeaves.map(leave => leave.emp_id))
        membersOnLeave = uniqueEmployeesOnLeave.size

        this.teamStats = {
          totalLeavesAvailed,
          totalWFHCount,
          totalRegularization,
          membersOnLeave,
        }
      } catch (err) {
        console.error('Error loading all team leaves:', err)
        this.allTeamLeaves = []
        this.teamStats = {
          totalLeavesAvailed: 0,
          totalWFHCount: 0,
          totalRegularization: 0,
          membersOnLeave: 0,
        }
      } finally {
        this.loadingLeaves = false
      }
    },
    async loadTeamMembers() {
      if (!this.canViewTeam) return

      try {
        if (this.canApplyOnBehalfAll) {
          const res = await userService.getAllUsers()
          let allUsers = []
          if (res && res.status === 'OK' && res.data) {
            allUsers = res.data || []
          } else if (res && res.data) {
            allUsers = Array.isArray(res.data) ? res.data : []
          }

          this.teamMembers = allUsers.filter(user => {
            const status = user.status || user.employee_status || user.is_active
            return status === 'Active' ||
                   status === 'active' ||
                   status === true ||
                   status === 'ACTIVE' ||
                   (status === undefined && user.emp_id)
          })
        } else if (this.isAccountManager) {
          const res = await userService.getAllTeamMembersList(this.emp_id)
          let teamMembers = []
          if (res && res.status === 'OK') {
            teamMembers = res.data || []
          } else if (res && res.data) {
            teamMembers = Array.isArray(res.data) ? res.data : []
          }

          this.teamMembers = teamMembers.filter(user => {
            const status = user.status || user.employee_status || user.is_active
            return status === 'Active' ||
                   status === 'active' ||
                   status === true ||
                   status === 'ACTIVE' ||
                   (status === undefined && user.emp_id)
          })
        }
      } catch (err) {
        console.error('Error loading team members:', err)
        this.teamMembers = []
      }
    },

    /**
     * Load probation records for scoped team members.
     * Merge getAllUsers (HR fields: doj / probation_days / end_date / probation_pending_days)
     * with /api/probation — do not early-return on sparse team-member payloads.
     */
    async loadProbationData() {
      if ((this.viewMode !== 'team' && this.viewMode !== 'org') || !this.teamMembers || this.teamMembers.length === 0) {
        this.probationData = []
        this.probationSourceMissing = true
        return
      }

      const teamEmpIdSet = new Set(
        (this.teamMembers || []).map(m => String(m.emp_id || m.id)).filter(Boolean)
      )

      const hasProbationSignal = (user) => {
        if (!user) return false
        const status = user.probation_status != null ? user.probation_status : user.probationStatus
        const statusLower = String(status != null ? status : '').trim().toLowerCase()
        // Explicit HR probation fields only — never invent people from bare DOJ.
        // Include Accepted/Rejected so we can exclude them from Alerts / People on Probation.
        return user.probation_days != null
          || user.probationDays != null
          || user.probation_pending_days != null
          || user.probationPendingDays != null
          || !!user.probation_end_date
          || !!user.probationEndDate
          || user.is_probation === true
          || user.isOnProbation === true
          || user.on_probation === true
          || ['extended', 'accepted', 'rejected', 'completed', 'active', 'pending'].includes(statusLower)
      }

      const mapUserToProbation = (user) => {
        const empId = user.emp_id || user.id || user.employee_id
        const doj = user.doj || user.date_of_joining || user.joining_date || user.start_date
        const probationDays = user.probation_days != null ? user.probation_days : user.probationDays
        const pendingDays = user.probation_pending_days != null
          ? user.probation_pending_days
          : user.probationPendingDays
        // Prefer explicit probation end; user.end_date is probation end in Manage Users list
        const endFromUser = user.probation_end_date
          || user.probationEndDate
          || user.end_date
          || user.endDate
        const computedEnd = (!endFromUser && doj && probationDays != null)
          ? moment(doj).add(Number(probationDays) || 0, 'days').format('YYYY-MM-DD')
          : null
        const endDate = endFromUser || computedEnd
        // Keep null probation_status as null (HR Manage Users = still on probation / Null).
        // Do NOT invent Active — snapshot allowlist is Null or Extended only.
        // Do NOT fall back to employment user.status (Active/Inactive).
        const rawStatus = user.probation_status != null
          ? user.probation_status
          : (user.probationStatus != null
            ? user.probationStatus
            : (user.status && ['Accepted', 'Rejected', 'Extended'].includes(user.status)
              ? user.status
              : null))
        const flagged = user.is_probation === true || user.isOnProbation === true || user.on_probation === true
        const status = rawStatus != null ? rawStatus : null
        const statusLower = status != null ? String(status).trim().toLowerCase() : ''
        const completed = ['accepted', 'rejected', 'completed', 'inactive', 'closed'].includes(statusLower)
        const openForSnapshot = !statusLower || statusLower === 'extended'
        return {
          emp_id: empId,
          employee_id: empId,
          doj,
          start_date: doj,
          probation_days: probationDays != null ? probationDays : null,
          probation_pending_days: pendingDays != null ? pendingDays : null,
          probation_status: status,
          status,
          // Snapshot countable = Null or Extended; keep flags for source merge only
          is_probation: !completed && (openForSnapshot || flagged),
          probation_end_date: endDate,
          end_date: endDate,
        }
      }

      const byEmpId = new Map()
      const upsert = (row) => {
        if (!row) return
        const empId = row.emp_id || row.employee_id
        if (!empId || !teamEmpIdSet.has(String(empId))) return
        const key = String(empId)
        const prev = byEmpId.get(key) || {}
        // Prefer explicit decision statuses from either source (Accepted/Rejected win over null)
        const mergedStatus = (() => {
          const candidates = [row.probation_status, row.status, prev.probation_status, prev.status]
          const decided = candidates.find(s => s != null && ['accepted', 'rejected', 'completed', 'extended'].includes(String(s).trim().toLowerCase()))
          if (decided != null) return decided
          return row.probation_status != null ? row.probation_status
            : (row.status != null ? row.status
              : (prev.probation_status != null ? prev.probation_status : prev.status))
        })()
        const statusLower = mergedStatus != null ? String(mergedStatus).trim().toLowerCase() : ''
        const completed = ['accepted', 'rejected', 'completed', 'inactive', 'closed'].includes(statusLower)
        const openForSnapshot = !statusLower || statusLower === 'extended'
        const merged = {
          ...prev,
          ...row,
          emp_id: empId,
          employee_id: empId,
          doj: row.doj || prev.doj || null,
          start_date: row.start_date || row.doj || prev.start_date || prev.doj || null,
          probation_days: row.probation_days != null ? row.probation_days : prev.probation_days,
          probation_pending_days: row.probation_pending_days != null
            ? row.probation_pending_days
            : prev.probation_pending_days,
          probation_status: mergedStatus,
          status: mergedStatus,
          probation_end_date: row.probation_end_date || prev.probation_end_date || null,
          end_date: row.end_date || row.probation_end_date || prev.end_date || prev.probation_end_date || null,
          is_probation: !completed && (
            openForSnapshot
            || !!(row.is_probation || prev.is_probation)
          ),
        }
        byEmpId.set(key, merged)
      }

      try {
        // 1) teamMembers when they already carry HR probation fields
        ;(this.teamMembers || []).filter(hasProbationSignal).map(mapUserToProbation).forEach(upsert)

        // 2) Full user list (canonical HR source — same as Manage Users)
        try {
          const res = await userService.getAllUsers()
          const allUsers = (res && res.data && Array.isArray(res.data))
            ? res.data
            : (res && Array.isArray(res.data) ? res.data : [])
          if (res && (res.status === 'OK' || res.status === 200 || Array.isArray(res.data))) {
            allUsers.filter(hasProbationSignal).map(mapUserToProbation).forEach(upsert)
          }
        } catch (usersErr) {
          console.error('Error loading users for probation data:', usersErr)
        }

        // 3) Probation service API — merge, don't replace
        try {
          const probationRes = await userProbationService.getAll()
          const body = probationRes && probationRes.data
          const payload = body && (Array.isArray(body) ? body : (body.data || body.probations || body.results))
          const allProbationData = Array.isArray(payload) ? payload : []
          allProbationData.forEach((probation) => {
            const mapped = mapUserToProbation({
              ...probation,
              emp_id: probation.emp_id || probation.employee_id,
              probation_status: probation.probation_status || probation.status,
              status: probation.status || probation.probation_status,
            })
            upsert(mapped)
          })
        } catch (probationErr) {
          console.error('Error loading probation data from probation service:', probationErr)
        }

        this.probationData = Array.from(byEmpId.values())
        this.probationSourceMissing = this.probationData.length === 0
      } catch (err) {
        console.error('Error loading probation data:', err)
        this.probationData = []
        this.probationSourceMissing = true
      }
    },
    async loadAccountManagers() {
      try {
        const res = await userService.getAllManagersList()
        if (res && res.status === 'OK' && res.data) {
          this.accountManagers = res.data.filter(manager => {
            const status = manager.status || manager.employee_status || manager.is_active
            return status === 'Active' || status === 'active' || status === true || status === 'ACTIVE'
          })
        } else if (res && res.data) {
          this.accountManagers = Array.isArray(res.data) ? res.data : []
        }
      } catch (err) {
        console.error('Error loading account managers:', err)
        this.accountManagers = []
      }
    },
    async handleAccountManagerChange() {
      if (!this.selectedAccountManager) {
        this.teamWiseEmployees = []
        this.selectedTeamWiseEmployee = null
        this.teamWiseAnalytics = {
          totalEmployees: 0,
          totalLeaves: 0,
          totalWFH: 0,
          pendingApprovals: 0,
          regularizationCases: 0,
        }
        await this.loadAllTeamLeaves()
        const targetEmpId = this.emp_id
        await Promise.all([
          this.loadEmployeeConfig(targetEmpId),
          this.loadLeaveStats(targetEmpId),
          this.loadBiometricData(targetEmpId),
          this.loadAttendanceData(targetEmpId),
          this.loadAnnouncements(),
          this.loadBirthdaysAndAnniversaries(),
        ])
        this.$nextTick(() => {
          this.updateLeaveBalanceChart()
        })
        return
      }

      try {
        const res = await userService.getAllUsers()
        let allUsers = []
        if (res && res.status === 'OK' && res.data) {
          allUsers = res.data || []
        } else if (res && res.data) {
          allUsers = Array.isArray(res.data) ? res.data : []
        } else if (Array.isArray(res)) {
          allUsers = res
        }

        const filteredEmployees = allUsers.filter(employee => {
          const employeeMgrId = employee.mgr_id || employee.manager_id || employee.manager || employee.account_manager
          return employeeMgrId === this.selectedAccountManager
        })

        this.teamWiseEmployees = filteredEmployees.filter(employee => {
          const status = employee.status || employee.employee_status || employee.is_active
          return status === 'Active' ||
                 status === 'active' ||
                 status === true ||
                 status === 'ACTIVE' ||
                 (status === undefined && employee.emp_id)
        })

        if (this.viewMode === 'org') {
          await this.loadData()
          await this.loadTeamWiseAnalytics()
          return
        }

        await this.loadAccountManagerTeamData()

        const targetEmpId = this.emp_id
        await Promise.all([
          this.loadEmployeeConfig(targetEmpId),
          this.loadLeaveStats(targetEmpId),
          this.loadBiometricData(targetEmpId),
          this.loadAttendanceData(targetEmpId),
          this.loadAnnouncements(),
          this.loadBirthdaysAndAnniversaries(),
        ])

        if (this.viewMode === 'team') {
          try {
            await this.loadTeamTodayAttendance()
          } catch (e) {
            this.teamTodayPunchRecords = []
          }
        }

        this.$nextTick(() => {
          this.updateLeaveBalanceChart()
        })

        await this.loadTeamWiseAnalytics()
      } catch (err) {
        console.error('Error loading team wise employees:', err)
        this.teamWiseEmployees = []
      }
    },
    async loadEmployeesForAccountManager(accountManagerId) {
      if (!accountManagerId) {
        this.teamWiseEmployeesForModal = []
        return
      }

      try {
        const res = await userService.getAllUsers()
        let allUsers = []
        if (res && res.status === 'OK' && res.data) {
          allUsers = res.data || []
        } else if (res && res.data) {
          allUsers = Array.isArray(res.data) ? res.data : []
        } else if (Array.isArray(res)) {
          allUsers = res
        }

        const filteredEmployees = allUsers.filter(employee => {
          const employeeMgrId = employee.mgr_id || employee.manager_id || employee.manager || employee.account_manager
          return employeeMgrId === accountManagerId
        })

        this.teamWiseEmployeesForModal = filteredEmployees.filter(employee => {
          const status = employee.status || employee.employee_status || employee.is_active
          return status === 'Active' ||
                 status === 'active' ||
                 status === true ||
                 status === 'ACTIVE' ||
                 (status === undefined && employee.emp_id)
        })
      } catch (err) {
        console.error('Error loading employees for account manager:', err)
        this.teamWiseEmployeesForModal = []
      }
    },
    async loadAccountManagerTeamData() {
      if (!this.selectedAccountManager || !this.teamWiseEmployees || this.teamWiseEmployees.length === 0) {
        this.allTeamLeaves = []
        return
      }

      try {
        const employeeIds = this.teamWiseEmployees.map(emp => emp.emp_id || emp.id).filter(id => id)

        const filtersForLeaves = this.activeFilters ? { ...this.activeFilters } : {}
        delete filtersForLeaves.status

        const leavePromises = employeeIds.map(async empId => {
          try {
            const res = await attendanceService.getMyLeaves(empId, filtersForLeaves)
            if (res && res.status === 'OK' && res.data) {
              return res.data.map(leave => ({ ...leave, emp_id: empId }))
            }
            return []
          } catch (err) {
            console.error(`Error loading leaves for ${empId}:`, err)
            return []
          }
        })

        const statsPromises = employeeIds.map(async empId => {
          try {
            const res = await attendanceService.getLeaveStats(empId)
            if (res && res.status === 'OK' && res.data) {
              return { emp_id: empId, ...res.data }
            }
            return { emp_id: empId, leaveBalance: 0, leavesTaken: 0, WFHCount: 0 }
          } catch (err) {
            console.error(`Error loading stats for ${empId}:`, err)
            return { emp_id: empId, leaveBalance: 0, leavesTaken: 0, WFHCount: 0 }
          }
        })

        const configPromises = employeeIds.map(async empId => {
          try {
            const res = await attendanceService.getEmployeeConfig(empId)
            if (res && res.status === 'OK' && res.data) {
              return { emp_id: empId, config: res.data }
            }
            return { emp_id: empId, config: null }
          } catch (err) {
            console.error(`Error loading config for ${empId}:`, err)
            return { emp_id: empId, config: null }
          }
        })

        const [leavesArrays, statsArrays, configArrays] = await Promise.all([
          Promise.all(leavePromises),
          Promise.all(statsPromises),
          Promise.all(configPromises),
        ])

        this.teamMemberLeaveBalances = statsArrays || []
        this.teamMemberConfigs = configArrays || []

        this.allTeamLeaves = leavesArrays
          .flat()
          .sort((a, b) => {
            const dateA = moment(a.from_date || a.applied_date || a.created_at)
            const dateB = moment(b.from_date || b.applied_date || b.created_at)
            return dateB.diff(dateA)
          })

        await this.loadRegularizationCases(this.selectedAccountManager)
      } catch (err) {
        console.error('Error loading account manager team data:', err)
        this.allTeamLeaves = []
      }
    },
    async handleTeamWiseEmployeeChange() {
      if (this.selectedTeamWiseEmployee) {
        this.currentViewEmpId = this.selectedTeamWiseEmployee
        await this.loadData()
      } else {
        this.currentViewEmpId = this.emp_id
        if (this.viewMode === 'org') {
          await this.loadData()
        } else {
          await this.loadAccountManagerTeamData()
          const targetEmpId = this.emp_id
          await Promise.all([
            this.loadEmployeeConfig(targetEmpId),
            this.loadLeaveStats(targetEmpId),
            this.loadBiometricData(targetEmpId),
            this.loadAttendanceData(targetEmpId),
            this.loadAnnouncements(),
            this.loadBirthdaysAndAnniversaries(),
          ])
          this.$nextTick(() => {
            this.updateLeaveBalanceChart()
          })
        }
      }
      await this.loadTeamWiseAnalytics()
    },
    async loadTeamWiseAnalytics() {
      if (!this.selectedAccountManager) {
        this.teamWiseAnalytics = {
          totalEmployees: 0,
          totalLeaves: 0,
          totalWFH: 0,
          pendingApprovals: 0,
          regularizationCases: 0,
        }
        return
      }

      try {
        const employeeIds = this.selectedTeamWiseEmployee
          ? [this.selectedTeamWiseEmployee]
          : this.teamWiseEmployees.map(emp => emp.emp_id || emp.id)

        this.teamWiseAnalytics.totalEmployees = employeeIds.length

        let totalLeaves = 0
        let totalWFH = 0
        let pendingApprovals = 0

        for (const empId of employeeIds) {
          const leaveRes = await attendanceService.getMyLeaves(empId)
          if (leaveRes && leaveRes.status === 'OK' && leaveRes.data) {
            const leaves = leaveRes.data
            leaves.forEach(leave => {
              if ((leave.status || '').toLowerCase() !== 'approved') return
              const leaveType = (leave.leave_type || leave.type || '').toLowerCase()
              const isWFH = isWFHLeaveType(leave.leave_type || leave.type)
              if (isWFH) {
                totalWFH += leave.total_days || 1
              } else {
                totalLeaves += leave.total_days || 1
              }
            })
            pendingApprovals += leaves.filter(l =>
              l.status === 'Pending Manager Approval' ||
              l.status === 'Manager Approved - Pending HR' ||
              l.status === 'Pending HR Approval',
            ).length
          }
        }

        let regularizationCases = 0
        const monthStart = moment().startOf('month').format('YYYY-MM-DD')
        const monthEnd = moment().endOf('month').format('YYYY-MM-DD')

        for (const empId of employeeIds) {
          const regRes = await attendanceService.getRegularizationCases(empId, monthStart, monthEnd)
          if (regRes && regRes.status === 'OK' && regRes.data) {
            regularizationCases += regRes.data.length
          }
        }

        this.teamWiseAnalytics = {
          totalEmployees: employeeIds.length,
          totalLeaves,
          totalWFH,
          pendingApprovals,
          regularizationCases,
        }
      } catch (err) {
        console.error('Error loading team wise analytics:', err)
      }
    },
    handleTeamMemberChange() {
      if (this.viewMode === 'team') {
        if (this.selectedTeamMember) {
          this.currentViewEmpId = this.selectedTeamMember
          this.allTeamLeaves = []
          this.loadData()
        } else {
          this.currentViewEmpId = this.emp_id
          this.loadData()
        }
      }
    },
  },
}
